import { Button, FileUpload, LoadingArea } from '@consigli/facade';
import { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { PrecheckResult } from '../types';
import { PrecheckValidationResults } from './precheck-validation-results';

interface PrecheckContentProps {
  precheckResult: PrecheckResult;
  localFiles: File[];
  setLocalFiles: Dispatch<SetStateAction<File[]>>;
  isUploading: boolean;
  progress: number;
  onGenerateReport: () => void;
  isStartingPipeline: boolean;
}

export const PrecheckContent: FC<PrecheckContentProps> = ({
  precheckResult,
  localFiles,
  setLocalFiles,
  isUploading,
  progress,
  onGenerateReport,
  isStartingPipeline,
}) => {
  const { t } = useTranslation();

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-semibold text-day-neutral-dark mb-2">
          {t('package-creation.reports.template-check-title', 'Template Analysis Complete')}
        </h1>
        <p className="text-day-neutral-subtle">
          {t(
            'package-creation.reports.summary-title',
            'Review the validation results and proceed to generate the report',
          )}
        </p>
      </div>

      <div className="mb-6">
        <PrecheckValidationResults result={precheckResult} />
      </div>

      <div className="mb-8 bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-day-neutral-dark mb-4">
          {t('document-upload.upload-additional-files', 'Upload Additional Files')}
        </h2>
        <p className="text-day-neutral-subtle mb-4">
          {t(
            'document-upload.upload-additional-files-description',
            'Upload more documents to address missing fields or improve report coverage.',
          )}
        </p>

        {isUploading ? (
          <LoadingArea
            className="mt-4"
            title={t('document-upload.uploading-files', 'Uploading files...')}
            message={
              progress < 1 ? t('document-upload.initializing', 'Initializing...') : `${progress}%`
            }
          />
        ) : (
          <FileUpload
            files={localFiles}
            setFiles={setLocalFiles}
            dropAreaLabel={t('document-upload.drag-and-drop', 'Drag and drop files here')}
            dropAreaClassName="h-32 w-full"
            fileButtonLabel={t('document-upload.browse-files', 'Browse Files')}
            folderButtonLabel={t('document-upload.browse-folders', 'Browse Folders')}
            uploadListLabel={t('document-upload.files', 'Files')}
            zeroByteFilesMessage={t('document-upload.zero-bytes', 'File has zero bytes')}
          />
        )}
      </div>

      <div className="flex justify-center">
        <Button rounded primary onClick={onGenerateReport} disabled={isStartingPipeline}>
          {isStartingPipeline
            ? t('package-creation.reports.starting', 'Starting...')
            : t('package-creation.reports.generate-report', 'Generate Report')}
        </Button>
      </div>
    </div>
  );
};
